/**
 * Billing store (Phase 3 — slice 1)
 *
 * Caches the current org's subscription + usage. Auto-refetches when the
 * active org switches.
 *
 * `lastPlanLimit` is set by `apiFetch` when any API call returns a 403 with
 * code `PLAN_LIMIT` — that drives the global UpgradePrompt modal.
 */

import { create } from 'zustand';
import type { PlanFeatures } from '../shared/plans';
import { PLAN_TIERS } from '../shared/plans';
import { useAuthStore } from './authStore';

export interface SubscriptionView {
  plan_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_end: string | null;
  cancel_at_period_end: boolean;
  /** True once the org has an active Lemon Squeezy customer (slice 2). */
  has_billing_customer?: boolean;
  features: PlanFeatures;
  usage: {
    sites: number;
    members: number;
    storage_gb: number | null;
  };
}

export interface PlanLimitNotice {
  /** Feature key from the backend (`maxSites`, `scada`, etc.). */
  feature: string;
  /** Plan id the org is on when the limit triggered. */
  planId: string;
  /** Human-readable reason (already localized from the backend). */
  message: string;
  /** Numeric limit when applicable. */
  limit?: number;
  /** Current usage when applicable. */
  current?: number;
}

interface BillingState {
  subscription: SubscriptionView | null;
  isLoading: boolean;
  error: string | null;
  lastPlanLimit: PlanLimitNotice | null;

  fetchSubscription: () => Promise<void>;
  /**
   * Calls POST /billing/create-checkout and returns a hosted checkout URL.
   * Caller is responsible for redirecting the browser to it.
   */
  createCheckout: (planId?: 'professional' | 'enterprise') => Promise<string>;
  /**
   * Returns a one-shot signed URL to the Lemon-hosted customer portal where
   * the subscriber updates card / cancels / changes plan.
   */
  createPortal: () => Promise<string>;
  setPlanLimit: (notice: PlanLimitNotice) => void;
  clearPlanLimit: () => void;
  reset: () => void;
}

export const useBillingStore = create<BillingState>((set) => ({
  subscription: null,
  isLoading: false,
  error: null,
  lastPlanLimit: null,

  fetchSubscription: async () => {
    const auth = useAuthStore.getState();
    if (!auth.isAuthenticated || !auth.currentOrg) {
      // No active org — clear cached data so we don't leak the previous org's
      // numbers into the UI after logout / org switch.
      set({ subscription: null, isLoading: false, error: null });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      // Lazy-import to avoid circular dep with apiFetch (which imports authStore).
      const { apiFetch, ApiError } = await import('../services/apiFetch');
      const data = await apiFetch<SubscriptionView>('/api/v2/billing/subscription');
      set({ subscription: data, isLoading: false });
      void ApiError; // tree-shake hint
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load subscription';
      set({ error: message, isLoading: false });
    }
  },

  createCheckout: async (planId = 'professional') => {
    const { apiFetch } = await import('../services/apiFetch');
    const result = await apiFetch<{ checkout_url: string; checkout_id: string }>(
      '/api/v2/billing/create-checkout',
      {
        method: 'POST',
        body: JSON.stringify({ plan_id: planId }),
      },
    );
    return result.checkout_url;
  },

  createPortal: async () => {
    const { apiFetch } = await import('../services/apiFetch');
    const result = await apiFetch<{ portal_url: string }>(
      '/api/v2/billing/create-portal',
      { method: 'POST' },
    );
    return result.portal_url;
  },

  setPlanLimit: (notice) => set({ lastPlanLimit: notice }),
  clearPlanLimit: () => set({ lastPlanLimit: null }),

  reset: () => set({
    subscription: null,
    isLoading: false,
    error: null,
    lastPlanLimit: null,
  }),
}));

// Refetch the subscription whenever the active org changes (login, switch,
// invitation accept). Logout clears the store via `reset()`.
let lastOrgId: string | null = useAuthStore.getState().currentOrg?.id ?? null;
let lastIsAuthenticated = useAuthStore.getState().isAuthenticated;
useAuthStore.subscribe((state) => {
  const orgId = state.currentOrg?.id ?? null;
  const isAuthenticated = state.isAuthenticated;

  if (lastIsAuthenticated && !isAuthenticated) {
    useBillingStore.getState().reset();
  }
  if (orgId !== lastOrgId) {
    if (orgId) {
      void useBillingStore.getState().fetchSubscription();
    } else {
      useBillingStore.getState().reset();
    }
  }

  lastIsAuthenticated = isAuthenticated;
  lastOrgId = orgId;
});

// Initial kick: if we already have an org from persisted auth, fetch on boot.
if (lastOrgId && lastIsAuthenticated) {
  void useBillingStore.getState().fetchSubscription();
}

/**
 * Convenience: derive plan features from the cached subscription, falling
 * back to `free` when none is loaded yet. Use this in components that need
 * to gate UI before the subscription has loaded.
 */
export function getCurrentFeatures(): PlanFeatures {
  const sub = useBillingStore.getState().subscription;
  return sub?.features ?? PLAN_TIERS.free;
}
